import * as React from "react";
import { LaunchResult, ToolCall } from "../../../api/document/ValidationAgent";
import { buildCoObjectSummaries } from "../../../utils/helpers/ContentControlHelper";
import { formatMessageDefault, formatMessageWithParamsDefault } from "../../../utils/helpers/LanguageHelper";
import { RunResult, RunStatusType } from "../../rules/RulesResultPanel";
import { getPalette } from "../../rules/RuleResultItem";
import { sanitizeHtml } from "../../generic/chat/sanitizeHtml";
import { newBotMsg, newBotSystemMsg } from "../../generic/chat/ChatBot";
import { CoObjectItem, CoObjectSummary } from "../../interfaces/CoObject";
import { Rule, RuleType } from "../../interfaces/Rule";
import { Tag } from "../../interfaces/Tag";
import { localizeErrorReason } from "./validationI18n";
import { remapScriptTagIds } from "../../../utils/helpers/TagMergeHelper";
import { extractTagIdsFromScript } from "../../../utils/helpers/TagsHelper";
import {
  CAPTION_RESULT_CANNOT_EXECUTE,
  CAPTION_RESULT_FAILED,
  CAPTION_RESULT_UNKNOWN_RULE,
  CAPTION_RESULT_VERIFIED,
  CAPTION_TOOLRUNNER_EDIT_TAG_INVALID_OBJECTS,
  CAPTION_TOOLRUNNER_EDIT_TAG_TARGET_NOT_FOUND,
  CAPTION_TOOLRUNNER_EDIT_TAG_ZERO_OBJECTS,
  CAPTION_TOOLRUNNER_EDIT_RULE_INVALID_TAGS,
  CAPTION_TOOLRUNNER_EDIT_RULE_TARGET_NOT_FOUND,
  CAPTION_TOOLRUNNER_EDIT_RULE_TYPE_CHANGE_FORBIDDEN,
  CAPTION_TOOLRUNNER_EDIT_RULE_TABLE_TOTALS_UNSUPPORTED,
} from "./validationCaptions";
import {
  addMessage,
  setLaunchRuleIds,
  setStagedRules,
  setStagedTags,
  StagedEdit,
  upsertStagedTagEdit,
  upsertStagedRuleEdit,
} from "../../../redux/feature/chat/askai-chat-slice";
import { AppDispatch } from "../../../redux/store/store";

export interface ToolRunnerContext {
  dispatch: AppDispatch;
  coObjects: CoObjectItem[];
  tagIdAliases: Map<string, string>;
  stagedRules: Rule[];
  committedRules: Rule[];
  stagedTags: Tag[];
  committedTags: Tag[];
  stagedTagEdits: StagedEdit<Tag>[];
  stagedRuleEdits: StagedEdit<Rule>[];
  lastRunResults: RunResult[];
  onLaunchRules: (ruleIds: string[]) => void;
  unresolvableTagErrors?: UnresolvableTagError[];
  deletedTagReferenceErrors?: DeletedTagReferenceError[];
}

export type ToolHandler = (params: Record<string, unknown>, context: ToolRunnerContext) => void | Promise<void>;

export interface UnresolvableTagError {
  tagId: string;
  tagLabel: string;
  invalidContentControlIds: number[];
}

export interface DeletedTagReferenceError {
  ruleId: string;
  deletedTagIds: string[];
}

export type ProcessToolCallsResult =
  | { success: true }
  | { success: false; unresolvableTagErrors: UnresolvableTagError[] }
  | { success: false; deletedTagReferenceErrors: DeletedTagReferenceError[] };

const toolHandlers: Record<string, ToolHandler> = {};

interface ToolTagObjectParams {
  objectType?: unknown;
  contentControlId?: unknown;
  row?: unknown;
  column?: unknown;
}

interface ToolTagParams {
  id?: unknown;
  label?: unknown;
  objects?: unknown;
}

interface ToolRuleParams {
  id?: unknown;
  title?: unknown;
  type?: unknown;
  script?: unknown;
  tagIds?: unknown;
}

interface ToolLaunchResultsParams {
  ruleId?: unknown;
  status?: unknown;
  interpretation?: unknown;
}

export interface LaunchResultChatEntry {
  status?: RunStatusType;
  interpretationHtml: string;
}

function buildLaunchResultKeys(entries: LaunchResultChatEntry[]): string[] {
  const keyCounts = new Map<string, number>();

  return entries.map((entry) => {
    const baseKey = `${entry.status ?? "unknown"}:${entry.interpretationHtml}`;
    const nextCount = (keyCounts.get(baseKey) ?? 0) + 1;
    keyCounts.set(baseKey, nextCount);
    return nextCount === 1 ? baseKey : `${baseKey}:${nextCount}`;
  });
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function asString(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined;
}

function asStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : [];
}

function getRuleTitle(runResult: RunResult): string {
  return (
    runResult.title ||
    formatMessageWithParamsDefault(CAPTION_RESULT_UNKNOWN_RULE, "{ruleId}", {
      ruleId: runResult.ruleId,
    })
  );
}

function isNumericHeuristicMatch(messageText: string): boolean {
  return (
    messageText.includes("non-numeric") ||
    messageText.includes("non numeric") ||
    messageText.includes("not numeric") ||
    messageText.includes("numeric")
  );
}

export function getRunResultErrorReason(runResult: RunResult): LaunchResult["errorReason"] | undefined {
  if (runResult.info.status !== "ERROR") {
    return undefined;
  }

  const consistencyTags = runResult.info.consistencyTags ?? [];

  if (consistencyTags.some((tag) => tag.errorKind === "MISSING_TAG")) {
    return "MISSING_TAG";
  }

  if (consistencyTags.some((tag) => tag.errorKind === "MISSING_OBJECTS")) {
    return "MISSING_OBJECTS";
  }

  const messageText = (runResult.info.message ?? "").toLowerCase();
  if (isNumericHeuristicMatch(messageText)) {
    return "NON_NUMERIC_VALUE";
  }

  return "SCRIPT_ERROR";
}

export function buildFallbackLaunchInterpretation(runResult: RunResult): string {
  const ruleTitle = getRuleTitle(runResult);

  switch (runResult.info.status) {
    case "VERIFIED":
      return formatMessageWithParamsDefault(CAPTION_RESULT_VERIFIED, "{ruleTitle} verified", { ruleTitle });
    case "FAILED":
      return formatMessageWithParamsDefault(CAPTION_RESULT_FAILED, "{ruleTitle} failed", { ruleTitle });
    // AskAI never streams: RUNNING/QUEUED/CANCELED aren't expected to reach this fallback, but are
    // listed explicitly so they can never silently report a VERIFIED/FAILED verdict — they share
    // the same safe "cannot execute" fallback as ERROR.
    case "ERROR":
    // falls through
    case "RUNNING":
    case "QUEUED":
    case "CANCELED":
    default:
      return formatMessageWithParamsDefault(
        CAPTION_RESULT_CANNOT_EXECUTE,
        "{ruleTitle} could not be executed because {errorReason}",
        {
          ruleTitle,
          errorReason: localizeErrorReason(getRunResultErrorReason(runResult)),
        }
      );
  }
}

export function createLaunchResultBadge(status: RunStatusType): React.ReactElement {
  const palette = getPalette(status);

  return React.createElement(
    "span",
    {
      "aria-hidden": true,
      style: {
        display: "inline-flex",
        width: 18,
        height: 18,
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "50%",
        background: palette.badgeBg,
        color: palette.badgeFg,
        fontSize: 11,
        fontWeight: 700,
        flex: "0 0 auto",
        marginTop: 2,
      },
    },
    palette.badgeGlyph
  );
}

export function createLaunchResultsMessage(entries: LaunchResultChatEntry[]) {
  const entryKeys = buildLaunchResultKeys(entries);
  return newBotMsg("", () =>
    React.createElement(
      "div",
      {
        style: {
          display: "flex",
          flexDirection: "column",
          gap: 8,
        },
      },
      entries.map((entry, entryIndex) =>
        React.createElement(
          "div",
          {
            key: entryKeys[entryIndex],
            style: {
              display: "flex",
              alignItems: "flex-start",
              gap: 8,
            },
          },
          entry.status ? createLaunchResultBadge(entry.status) : null,
          React.createElement("div", {
            style: {
              flex: 1,
              minWidth: 0,
            },
            dangerouslySetInnerHTML: {
              __html: sanitizeHtml(entry.interpretationHtml),
            },
          })
        )
      )
    )
  );
}

function toInteger(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isInteger(value)) {
    return value;
  }

  if (typeof value === "string" && /^\d+$/.test(value.trim())) {
    return Number(value);
  }

  return undefined;
}

function sanitizeToolId(rawValue: string, prefix: "tag_" | "rule_"): string | null {
  const normalizedValue = rawValue
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_]/g, "")
    .replace(/_+/g, "_");

  const prefixedValue =
    normalizedValue && /^\d/.test(normalizedValue) ? `${prefix}${normalizedValue}` : normalizedValue;
  const truncatedValue = prefixedValue.slice(0, 256);

  if (!truncatedValue) {
    return null;
  }

  return /^[a-z_][a-z0-9_]{0,255}$/.test(truncatedValue) ? truncatedValue : null;
}

function ensureUniqueId(baseId: string, usedIds: Set<string>): string {
  let candidate = baseId;
  let suffix = 2;

  while (usedIds.has(candidate.toLowerCase())) {
    const suffixText = `_${suffix}`;
    candidate = `${baseId.slice(0, Math.max(0, 256 - suffixText.length))}${suffixText}`;
    suffix += 1;
  }

  usedIds.add(candidate.toLowerCase());
  return candidate;
}

function resolveRawToolTags(params: Record<string, unknown>): ToolTagParams[] {
  const rawTags = params.tags;
  return Array.isArray(rawTags) ? rawTags.filter((item): item is ToolTagParams => isRecord(item)) : [];
}

function resolveRawToolRules(params: Record<string, unknown>): ToolRuleParams[] {
  const rawRules = params.rules;
  return Array.isArray(rawRules) ? rawRules.filter((item): item is ToolRuleParams => isRecord(item)) : [];
}

async function resolveTagObjects(
  rawObjects: ToolTagObjectParams[],
  coObjectsByContentControlId: Map<number, CoObjectItem>
): Promise<CoObjectSummary[]> {
  const objectLookupIds: string[] = [];

  rawObjects.forEach((rawObject) => {
    const contentControlId = toInteger(rawObject.contentControlId);
    if (contentControlId === undefined) {
      console.warn("[ToolRunner] propose_tags: invalid contentControlId, dropping object", rawObject);
      return;
    }

    const matchedObject = coObjectsByContentControlId.get(contentControlId);
    if (!matchedObject?.id) {
      console.warn(
        `[ToolRunner] propose_tags: contentControlId ${contentControlId} could not be resolved, dropping object`
      );
      return;
    }

    const objectType = asString(rawObject.objectType)?.toUpperCase();
    const row = toInteger(rawObject.row);
    const column = toInteger(rawObject.column);
    const isTableCell = objectType === "TABLE_CELL" || row !== undefined || column !== undefined;

    if (isTableCell) {
      if (row === undefined || column === undefined) {
        console.warn(
          `[ToolRunner] propose_tags: contentControlId ${contentControlId} is missing row/column for TABLE_CELL, dropping object`
        );
        return;
      }

      objectLookupIds.push(`${matchedObject.id}_${row}-${column}`);
      return;
    }

    objectLookupIds.push(matchedObject.id);
  });

  if (objectLookupIds.length === 0) {
    return [];
  }

  const summaries = await buildCoObjectSummaries(Array.from(new Set(objectLookupIds)));
  return objectLookupIds
    .map((lookupId) => summaries[lookupId])
    .filter((summary): summary is CoObjectSummary => Boolean(summary));
}

function findMatchingCommittedTag(committedTags: Tag[], originalId: string, label: string): Tag | undefined {
  if (originalId) {
    const byId = committedTags.find((t) => t.id.toLowerCase() === originalId.toLowerCase());
    if (byId) return byId;
  }
  return label ? committedTags.find((t) => t.label === label) : undefined;
}

type RawTagResolution =
  | { type: "resolved"; tag: Tag; aliases: Array<[string, string]> }
  | { type: "error"; error: UnresolvableTagError }
  | { type: "skip" };

async function resolveRawTagEntry(
  rawTag: ToolTagParams,
  context: ToolRunnerContext,
  coObjectsByContentControlId: Map<number, CoObjectItem>,
  usedIds: Set<string>
): Promise<RawTagResolution> {
  const label = asString(rawTag.label)?.trim() ?? "";
  const originalId = asString(rawTag.id)?.trim() ?? "";

  const matchingCommittedTag = findMatchingCommittedTag(context.committedTags, originalId, label);

  const baseIdSource = originalId || label;
  const sanitizedBaseId = baseIdSource ? sanitizeToolId(baseIdSource, "tag_") : null;

  if (!matchingCommittedTag && !sanitizedBaseId) {
    console.warn("[ToolRunner] propose_tags: tag id sanitization produced an empty value, dropping tag", rawTag);
    return { type: "skip" };
  }

  const logId = matchingCommittedTag ? matchingCommittedTag.id : sanitizedBaseId;

  const rawObjectsToResolve = Array.isArray(rawTag.objects)
    ? rawTag.objects.filter((item): item is ToolTagObjectParams => isRecord(item))
    : [];
  const resolvedObjects = await resolveTagObjects(rawObjectsToResolve, coObjectsByContentControlId);

  if (resolvedObjects.length === 0 && rawObjectsToResolve.length > 0) {
    console.warn(`[ToolRunner] propose_tags: tag "${logId}" has no resolvable objects, collecting error`);
    const invalidIds = rawObjectsToResolve
      .map((obj) => toInteger(obj.contentControlId))
      .filter((id): id is number => id !== undefined);
    return {
      type: "error",
      error: { tagId: logId ?? originalId, tagLabel: label, invalidContentControlIds: invalidIds },
    };
  }

  if (resolvedObjects.length === 0) {
    console.debug(`[ToolRunner] propose_tags: tag "${logId}" has empty objects, skipping`);
    return { type: "skip" };
  }

  console.debug(`[AskAI:ToolRunner] propose_tags: tag "${logId}" resolved ${resolvedObjects.length} objects`);

  let uniqueId: string;
  if (matchingCommittedTag) {
    uniqueId = matchingCommittedTag.id;
    usedIds.add(uniqueId.toLowerCase());
  } else if (sanitizedBaseId) {
    uniqueId = ensureUniqueId(sanitizedBaseId, usedIds);
  } else {
    return { type: "skip" }; // unreachable; guarded above
  }

  const aliases: Array<[string, string]> = [];
  if (originalId) {
    aliases.push([originalId, uniqueId]);
  }
  if (sanitizedBaseId && sanitizedBaseId !== uniqueId) {
    aliases.push([sanitizedBaseId, uniqueId]);
  }

  return { type: "resolved", tag: { id: uniqueId, label, objects: resolvedObjects }, aliases };
}

registerToolHandler("show_message", (params, context) => {
  const text = asString(params.text);
  if (!text) {
    console.warn("[ToolRunner] show_message: missing text param");
    return;
  }

  context.dispatch(addMessage(newBotMsg(sanitizeHtml(text))));
});

registerToolHandler("propose_tags", async (params, context) => {
  const rawTags = resolveRawToolTags(params);
  console.info("[AskAI:ToolRunner] propose_tags: processing", rawTags.length, "tags");
  const usedIds = new Set<string>();
  const coObjectsByContentControlId = new Map<number, CoObjectItem>(
    context.coObjects
      .filter(
        (coObject): coObject is CoObjectItem & { contentControlId: number } => coObject.contentControlId !== undefined
      )
      .map((coObject) => [coObject.contentControlId, coObject])
  );
  const resolvedTags: Tag[] = [];
  const unresolvableErrors: UnresolvableTagError[] = [];

  // When FAB references an existing committed tag, preserve its real ID
  // instead of sanitizing it (sanitization lowercases and mangles the ID).
  // Fallback: match by exact label when ID doesn't match (FAB may use label as ID).
  // Also alias the sanitized variant so rule scripts using either form resolve correctly.
  for (const rawTag of rawTags) {
    const entry = await resolveRawTagEntry(rawTag, context, coObjectsByContentControlId, usedIds);
    if (entry.type === "error") {
      unresolvableErrors.push(entry.error);
    } else if (entry.type === "resolved") {
      for (const [from, to] of entry.aliases) {
        context.tagIdAliases.set(from, to);
      }
      resolvedTags.push(entry.tag);
    }
  }

  if (unresolvableErrors.length > 0) {
    console.warn("[AskAI:ToolRunner] propose_tags: some tags have unresolvable objects, aborting tool call processing");
    context.unresolvableTagErrors = unresolvableErrors;
    return;
  }

  console.info(
    "[AskAI:ToolRunner] propose_tags: resolved",
    resolvedTags.length,
    "tags with objects (dropped",
    rawTags.length - resolvedTags.length,
    "without objects)"
  );
  context.dispatch(setStagedTags(resolvedTags));
  context.stagedTags = resolvedTags;
});

/**
 * Merges two CoObjectSummary arrays by deduplicating on `id`.
 * Keeps all existing objects and appends only genuinely-new incoming ones —
 * mirrors the same logic in processExistingTag (AskAiModalHook) used for the
 * propose_tags save path. Must stay in sync if that logic changes.
 */
function mergeTagObjectsById(existing: CoObjectSummary[], incoming: CoObjectSummary[]): CoObjectSummary[] {
  const existingIds = new Set(existing.map((o) => o.id).filter(Boolean));
  const newOnly = incoming.filter((o) => o.id && !existingIds.has(o.id));
  return [...existing, ...newOnly];
}

/**
 * Computes the updated label and objects for a tag edit, picking the new value when provided.
 * When the agent supplies objects, they are MERGED with the base tag's existing objects
 * (dedup by id) rather than replacing them. This matches the propose_tags save-path behavior
 * and prevents existing objects from being silently dropped when the agent only provides the
 * delta (the new objects to add).
 */
function buildTagPatchFields(
  base: Tag,
  labelProvided: boolean,
  newLabel: string | undefined,
  objectsProvided: boolean,
  resolvedObjects: CoObjectSummary[]
): Pick<Tag, "label" | "objects"> {
  return {
    label: labelProvided ? (newLabel ?? base.label) : base.label,
    // Merge incoming objects with the current base objects so that consecutive agent edits
    // accumulate correctly and no existing object is lost when the agent provides only the
    // new objects to add (fix for: objects replaced instead of merged — edit_tag regression).
    objects: objectsProvided ? mergeTagObjectsById(base.objects, resolvedObjects) : base.objects,
  };
}

/**
 * Applies a tag edit to the appropriate target (staged tag or committed tag staged-edit),
 * or dispatches an error message when the target is not found / is deleted.
 */
function applyEditTagToTarget(
  context: ToolRunnerContext,
  resolvedId: string,
  labelProvided: boolean,
  newLabel: string | undefined,
  objectsProvided: boolean,
  resolvedObjects: CoObjectSummary[]
): void {
  // Staged-first: if the tag is in the staged set, mutate it in place
  const stagedTag = context.stagedTags.find((t) => t.id === resolvedId);
  if (stagedTag) {
    const patchFields = buildTagPatchFields(stagedTag, labelProvided, newLabel, objectsProvided, resolvedObjects);
    const updatedStagedTags = context.stagedTags.map((t) => (t.id === resolvedId ? { ...t, ...patchFields } : t));
    context.dispatch(setStagedTags(updatedStagedTags));
    context.stagedTags = updatedStagedTags;
    return;
  }

  // Committed: upsert a staged edit, preserving capturedVersion from any prior edit
  const committedTag = context.committedTags.find((t) => t.id === resolvedId);
  if (committedTag && committedTag.isDeleted !== true) {
    const existingEdit = context.stagedTagEdits.find((e) => e.targetId === resolvedId);
    const base: Tag = existingEdit ? existingEdit.patch : committedTag;
    const patch: Tag = {
      ...base,
      ...buildTagPatchFields(base, labelProvided, newLabel, objectsProvided, resolvedObjects),
    };
    const capturedVersion = existingEdit ? existingEdit.capturedVersion : committedTag;
    context.dispatch(upsertStagedTagEdit({ targetId: resolvedId, patch, capturedVersion }));
    return;
  }

  // Not found or deleted committed tag
  context.dispatch(
    addMessage(
      newBotSystemMsg(
        formatMessageWithParamsDefault(
          CAPTION_TOOLRUNNER_EDIT_TAG_TARGET_NOT_FOUND,
          "Tag id '{tagId}' was not found or has been deleted.",
          { tagId: resolvedId }
        )
      )
    )
  );
}

registerToolHandler("edit_tag", async (params, context) => {
  const id = asString(params.id)?.trim();
  if (!id) {
    console.warn("[ToolRunner] edit_tag: missing required param (id)");
    return;
  }

  const labelProvided = params.label !== undefined && params.label !== null;
  const newLabel = labelProvided ? asString(params.label) : undefined;
  const objectsProvided = params.objects !== undefined && params.objects !== null;

  if (!labelProvided && !objectsProvided) {
    console.warn("[ToolRunner] edit_tag: no fields to change (label and objects are both null)");
    return;
  }

  const rawObjects =
    objectsProvided && Array.isArray(params.objects)
      ? params.objects.filter((item): item is ToolTagObjectParams => isRecord(item))
      : [];

  if (objectsProvided && rawObjects.length === 0) {
    context.dispatch(
      addMessage(
        newBotSystemMsg(
          formatMessageDefault(CAPTION_TOOLRUNNER_EDIT_TAG_ZERO_OBJECTS, "Tag edit must contain at least one object.")
        )
      )
    );
    return;
  }

  const coObjectsByContentControlId = new Map(
    context.coObjects
      .filter((coObject) => coObject.contentControlId !== undefined)
      .map((coObject) => [coObject.contentControlId, coObject])
  );

  const resolvedObjects = objectsProvided ? await resolveTagObjects(rawObjects, coObjectsByContentControlId) : [];

  if (objectsProvided && resolvedObjects.length < rawObjects.length) {
    context.dispatch(
      addMessage(
        newBotSystemMsg(
          formatMessageDefault(
            CAPTION_TOOLRUNNER_EDIT_TAG_INVALID_OBJECTS,
            "Tag edit cannot be applied because one or more object references are invalid."
          )
        )
      )
    );
    return;
  }

  const resolvedId = context.tagIdAliases.get(id) ?? id;
  applyEditTagToTarget(context, resolvedId, labelProvided, newLabel, objectsProvided, resolvedObjects);
});

/**
 * Returns a DeletedTagReferenceError when the rule's script (CUSTOM) or tagIds (CONSISTENCY)
 * reference tag IDs that exist in committedTags with isDeleted:true and are not overridden
 * by a same-response propose_tags (i.e. present in context.stagedTags). Returns null when clean.
 */
function findDeletedTagReferences(
  ruleId: string,
  ruleType: RuleType,
  script: string,
  tagIds: string[],
  context: ToolRunnerContext
): DeletedTagReferenceError | null {
  // Tags in stagedTags override the committed deleted state (agent re-proposed them)
  const stagedTagIds = new Set(context.stagedTags.map((t) => t.id));

  const deletedIdSet = new Set(
    context.committedTags.filter((t) => t.isDeleted === true && !stagedTagIds.has(t.id)).map((t) => t.id)
  );

  if (deletedIdSet.size === 0) return null;

  const referencedIds = ruleType === RuleType.CONSISTENCY ? tagIds : extractTagIdsFromScript(script);

  const offendingIds = referencedIds.filter((id) => deletedIdSet.has(id));

  if (offendingIds.length === 0) return null;

  return { ruleId, deletedTagIds: offendingIds };
}

registerToolHandler("propose_rules", (params, context) => {
  const rawRules = resolveRawToolRules(params);
  console.info("[AskAI:ToolRunner] propose_rules: processing", rawRules.length, "rules");
  const usedIds = new Set<string>();
  const resolvedRules: Rule[] = [];
  const deletedTagErrors: DeletedTagReferenceError[] = [];

  rawRules.forEach((rawRule) => {
    const title = asString(rawRule.title)?.trim() ?? "";
    const originalId = asString(rawRule.id)?.trim() ?? "";

    let uniqueId: string;
    if (originalId) {
      const sanitizedBaseId = sanitizeToolId(originalId, "rule_");
      if (!sanitizedBaseId) {
        console.warn(
          "[ToolRunner] propose_rules: rule id sanitization produced an empty value, dropping rule",
          rawRule
        );
        return;
      }
      uniqueId = ensureUniqueId(sanitizedBaseId, usedIds);
    } else {
      // New rule with no id — leave id as "" so draftKey becomes "review-rule-N-new"
      uniqueId = "";
    }

    const ruleTypeValue = asString(rawRule.type)?.toUpperCase();
    let type: RuleType | undefined;
    if (ruleTypeValue === RuleType.CONSISTENCY) {
      type = RuleType.CONSISTENCY;
    } else if (ruleTypeValue === RuleType.CUSTOM) {
      type = RuleType.CUSTOM;
    }

    if (!type) {
      console.warn(
        `[ToolRunner] propose_rules: invalid rule type for "${originalId || "(no id)"}", dropping rule`,
        rawRule
      );
      return;
    }

    const remappedTagIds = asStringArray(rawRule.tagIds).map((tagId) => context.tagIdAliases.get(tagId) ?? tagId);
    const remappedScript = remapScriptTagIds(asString(rawRule.script) ?? "", context.tagIdAliases);

    // Validate: reject rules that reference deleted tag IDs
    const deletedError = findDeletedTagReferences(
      uniqueId || originalId || "(unnamed)",
      type,
      remappedScript,
      remappedTagIds,
      context
    );
    if (deletedError) {
      console.warn(
        `[ToolRunner] propose_rules: rule "${deletedError.ruleId}" references deleted tags: [${deletedError.deletedTagIds.join(", ")}]`
      );
      deletedTagErrors.push(deletedError);
      return;
    }

    resolvedRules.push({
      id: uniqueId,
      title,
      type,
      version: 0,
      script: type === RuleType.CUSTOM ? remappedScript : "",
      tagIds: type === RuleType.CONSISTENCY ? remappedTagIds : [],
    });
  });

  if (deletedTagErrors.length > 0) {
    console.warn("[AskAI:ToolRunner] propose_rules: rejected due to deleted tag references");
    context.deletedTagReferenceErrors = deletedTagErrors;
    return;
  }

  console.info("[AskAI:ToolRunner] propose_rules: resolved", resolvedRules.length, "rules");
  context.dispatch(setStagedRules(resolvedRules));
  context.stagedRules = resolvedRules;
});

type RulePatchFields = { tagIds: string[]; script: string };

/** Builds the type-specific patch fields for edit_rule. Returns null when tagId validation fails. */
function buildRulePatchFields(
  params: Record<string, unknown>,
  resolvedRule: Rule,
  context: ToolRunnerContext
): RulePatchFields | null {
  if (resolvedRule.type === RuleType.CONSISTENCY) {
    let tagIds = resolvedRule.tagIds ?? [];
    if (params.tagIds !== undefined && params.tagIds !== null) {
      const rawTagIds = asStringArray(params.tagIds);
      const remappedTagIds = rawTagIds.map((tagId) => context.tagIdAliases.get(tagId) ?? tagId);
      const validTagIds = new Set([
        ...context.stagedTags.map((t) => t.id),
        ...context.committedTags.filter((t) => t.isDeleted !== true).map((t) => t.id),
        ...context.stagedTagEdits.map((e) => e.targetId),
      ]);
      if (remappedTagIds.some((tagId) => !validTagIds.has(tagId))) {
        return null;
      }
      tagIds = remappedTagIds;
    }
    return { tagIds, script: "" };
  }
  // CUSTOM rule
  let script = resolvedRule.script;
  const rawScript = asString(params.script);
  if (rawScript !== undefined) {
    script = remapScriptTagIds(rawScript, context.tagIdAliases);
  }
  return { tagIds: [], script };
}

/**
 * Pre-validates an edit_rule call for deleted tag references, setting
 * `context.deletedTagReferenceErrors` and returning true when a violation is found.
 * Must run before `buildRulePatchFields` because that function already excludes deleted
 * tags from its valid-set (treating them as "not found"); we need to distinguish
 * "exists but deleted" from "never existed" to give the agent a meaningful retry message.
 */
function checkEditRuleDeletedTagReferences(
  params: Record<string, unknown>,
  resolvedId: string,
  resolvedRule: Rule,
  context: ToolRunnerContext
): boolean {
  if (resolvedRule.type === RuleType.CONSISTENCY) {
    if (params.tagIds !== undefined && params.tagIds !== null) {
      const preRemappedTagIds = asStringArray(params.tagIds).map((id) => context.tagIdAliases.get(id) ?? id);
      const deletedError = findDeletedTagReferences(resolvedId, RuleType.CONSISTENCY, "", preRemappedTagIds, context);
      if (deletedError) {
        console.warn(
          `[ToolRunner] edit_rule: rule "${resolvedId}" references deleted tags: [${deletedError.deletedTagIds.join(", ")}]`
        );
        context.deletedTagReferenceErrors = [deletedError];
        return true;
      }
    }
  } else if (params.script !== undefined) {
    const preRemappedScript = remapScriptTagIds(asString(params.script) ?? "", context.tagIdAliases);
    const deletedError = findDeletedTagReferences(resolvedId, RuleType.CUSTOM, preRemappedScript, [], context);
    if (deletedError) {
      console.warn(
        `[ToolRunner] edit_rule: rule "${resolvedId}" references deleted tags: [${deletedError.deletedTagIds.join(", ")}]`
      );
      context.deletedTagReferenceErrors = [deletedError];
      return true;
    }
  }
  return false;
}

/** Dispatches staged or committed upsert for a completed rule patch. */
function applyRulePatch(
  context: ToolRunnerContext,
  resolvedId: string,
  resolvedRule: Rule,
  stagedRule: Rule | undefined,
  patch: Rule
): void {
  if (stagedRule) {
    const updatedStagedRules = context.stagedRules.map((r) => (r.id === resolvedId ? patch : r));
    context.dispatch(setStagedRules(updatedStagedRules));
    context.stagedRules = updatedStagedRules;
  } else {
    const existingEdit = context.stagedRuleEdits.find((e) => e.targetId === resolvedId);
    const capturedVersion = existingEdit ? existingEdit.capturedVersion : resolvedRule;
    context.dispatch(upsertStagedRuleEdit({ targetId: resolvedId, patch, capturedVersion }));
  }
}

registerToolHandler("edit_rule", (params, context) => {
  const id = asString(params.id)?.trim();
  if (!id) {
    console.warn("[ToolRunner] edit_rule: missing required param (id)");
    return;
  }

  const resolvedId = context.tagIdAliases.get(id) ?? id;

  // Resolve target: staged-first, then committed
  const stagedRule = context.stagedRules.find((r) => r.id === resolvedId);
  const committedRule = context.committedRules.find((r) => r.id === resolvedId);
  const resolvedRule = stagedRule ?? committedRule;

  if (!resolvedRule) {
    context.dispatch(
      addMessage(
        newBotSystemMsg(
          formatMessageWithParamsDefault(
            CAPTION_TOOLRUNNER_EDIT_RULE_TARGET_NOT_FOUND,
            "Rule id '{ruleId}' was not found.",
            { ruleId: resolvedId }
          )
        )
      )
    );
    return;
  }

  // Type-change check: type changes are forbidden
  const rawType = asString(params.type);
  if (rawType && rawType.toUpperCase() !== resolvedRule.type) {
    context.dispatch(
      addMessage(
        newBotSystemMsg(
          formatMessageDefault(
            CAPTION_TOOLRUNNER_EDIT_RULE_TYPE_CHANGE_FORBIDDEN,
            "Rule type cannot be changed via edit_rule. Delete and recreate the rule instead."
          )
        )
      )
    );
    return;
  }

  // TABLE_TOTALS rules are not yet supported for editing (IDS-5906)
  if (resolvedRule.type === RuleType.TABLE_TOTALS) {
    context.dispatch(
      addMessage(
        newBotSystemMsg(
          formatMessageDefault(
            CAPTION_TOOLRUNNER_EDIT_RULE_TABLE_TOTALS_UNSUPPORTED,
            "Table Totals rules cannot be edited. This rule type is not yet supported."
          )
        )
      )
    );
    return;
  }

  if (checkEditRuleDeletedTagReferences(params, resolvedId, resolvedRule, context)) {
    return;
  }

  // Build patch: title defaults to current; type-specific fields via helper
  const newTitle = asString(params.title)?.trim() ?? resolvedRule.title;
  const patchFields = buildRulePatchFields(params, resolvedRule, context);
  if (patchFields === null) {
    context.dispatch(
      addMessage(
        newBotSystemMsg(
          formatMessageDefault(
            CAPTION_TOOLRUNNER_EDIT_RULE_INVALID_TAGS,
            "Rule edit references tags that do not exist."
          )
        )
      )
    );
    return;
  }

  const patch: Rule = { ...resolvedRule, title: newTitle, ...patchFields };
  applyRulePatch(context, resolvedId, resolvedRule, stagedRule, patch);
});

registerToolHandler("launch_rules", (params, context) => {
  const requestedRuleIds = asStringArray(params.ruleIds);
  console.info("[AskAI:ToolRunner] launch_rules: requested rule IDs:", requestedRuleIds);
  const availableRuleIds = new Set([...context.stagedRules, ...context.committedRules].map((rule) => rule.id));
  const validRuleIds = requestedRuleIds.filter(
    (ruleId, index) => availableRuleIds.has(ruleId) && requestedRuleIds.indexOf(ruleId) === index
  );
  const invalidRuleIds = requestedRuleIds.filter((ruleId) => !availableRuleIds.has(ruleId));

  if (invalidRuleIds.length > 0) {
    console.warn(`[ToolRunner] launch_rules: invalid ruleIds skipped: ${invalidRuleIds.join(", ")}`);
  }

  const allRules = [...context.stagedRules, ...context.committedRules];
  const tableTotalsRuleIds = validRuleIds.filter(
    (ruleId) => allRules.find((rule) => rule.id === ruleId)?.type === RuleType.TABLE_TOTALS
  );
  const launchableRuleIds = validRuleIds.filter((ruleId) => !tableTotalsRuleIds.includes(ruleId));

  if (tableTotalsRuleIds.length > 0) {
    console.warn(`[ToolRunner] launch_rules: TABLE_TOTALS ruleIds skipped: ${tableTotalsRuleIds.join(", ")}`);
  }

  if (launchableRuleIds.length === 0) {
    console.warn("[ToolRunner] launch_rules: no valid ruleIds resolved, hiding launch button");
    context.dispatch(setLaunchRuleIds([]));
    return;
  }

  context.dispatch(setLaunchRuleIds(launchableRuleIds));
});

/** Classifies one raw result into interpretationsByRuleId or unmatchedInterpretations. */
function classifyLaunchResultInterpretation(
  rawResult: ToolLaunchResultsParams,
  lastRunResults: RunResult[],
  ignoreExtraInterpretations: boolean,
  interpretationsByRuleId: Map<string, string>,
  unmatchedInterpretations: string[]
): void {
  const ruleId = asString(rawResult.ruleId);
  const interpretation = asString(rawResult.interpretation)?.trim();
  if (!interpretation) {
    return;
  }
  if (ruleId && lastRunResults.some((runResult) => runResult.ruleId === ruleId)) {
    if (!interpretationsByRuleId.has(ruleId)) {
      interpretationsByRuleId.set(ruleId, interpretation);
    }
    return;
  }
  if (ignoreExtraInterpretations) {
    console.warn(`[ToolRunner] show_launch_results: ignoring unmatched interpretation for ruleId "${ruleId ?? ""}"`);
    return;
  }
  unmatchedInterpretations.push(interpretation);
}

/** Builds the full list of chat entries from raw agent results and last-run results. */
function buildLaunchResultEntries(
  rawResults: ToolLaunchResultsParams[],
  lastRunResults: RunResult[]
): LaunchResultChatEntry[] {
  const interpretationsByRuleId = new Map<string, string>();
  const unmatchedInterpretations: string[] = [];
  const ignoreExtraInterpretations = rawResults.length > lastRunResults.length;

  rawResults.forEach((rawResult) =>
    classifyLaunchResultInterpretation(
      rawResult,
      lastRunResults,
      ignoreExtraInterpretations,
      interpretationsByRuleId,
      unmatchedInterpretations
    )
  );

  const entries: LaunchResultChatEntry[] = lastRunResults.map((runResult) => ({
    status: runResult.info.status,
    interpretationHtml: interpretationsByRuleId.get(runResult.ruleId) ?? buildFallbackLaunchInterpretation(runResult),
  }));

  unmatchedInterpretations.forEach((interpretationHtml) => {
    entries.push({ interpretationHtml });
  });

  return entries;
}

registerToolHandler("show_launch_results", (params, context) => {
  const rawResults = Array.isArray(params.results)
    ? params.results.filter((item): item is ToolLaunchResultsParams => isRecord(item))
    : [];
  console.info("[AskAI:ToolRunner] show_launch_results: processing", rawResults.length, "results");

  if (rawResults.length === 0) {
    return;
  }

  const entries = buildLaunchResultEntries(rawResults, context.lastRunResults);

  if (entries.length === 0) {
    return;
  }

  context.dispatch(addMessage(createLaunchResultsMessage(entries)));
});

export function registerToolHandler(toolName: string, handler: ToolHandler): void {
  toolHandlers[toolName] = handler;
}

/** Looks up and invokes a single tool handler, swallowing errors so the loop can continue. */
async function executeToolCall(toolCall: ToolCall, context: ToolRunnerContext): Promise<void> {
  const handler = toolHandlers[toolCall.tool];
  if (!handler) {
    console.warn(`[ToolRunner] Unknown tool: "${toolCall.tool}", skipping`);
    return;
  }
  if (!toolCall.params || typeof toolCall.params !== "object") {
    console.warn(`[ToolRunner] Invalid params for tool "${toolCall.tool}", skipping`);
    return;
  }
  try {
    await handler(toolCall.params, context);
  } catch (error) {
    console.error(`[ToolRunner] Error executing tool "${toolCall.tool}":`, error);
  }
}

export async function processToolCalls(
  toolCalls: ToolCall[],
  context: ToolRunnerContext
): Promise<ProcessToolCallsResult> {
  console.info(
    "[AskAI:ToolRunner] processToolCalls: processing",
    toolCalls.length,
    "tool calls:",
    toolCalls.map((tc) => tc.tool)
  );
  for (const toolCall of toolCalls) {
    await executeToolCall(toolCall, context);
    if (context.unresolvableTagErrors?.length) {
      console.warn("[AskAI:ToolRunner] Stopping tool call processing due to unresolvable tag errors");
      break;
    }
    if (context.deletedTagReferenceErrors?.length) {
      console.warn("[AskAI:ToolRunner] Stopping tool call processing due to deleted tag reference errors");
      break;
    }
  }
  if (context.unresolvableTagErrors?.length) {
    return { success: false, unresolvableTagErrors: context.unresolvableTagErrors };
  }
  if (context.deletedTagReferenceErrors?.length) {
    return { success: false, deletedTagReferenceErrors: context.deletedTagReferenceErrors };
  }
  return { success: true };
}
